import React, { useState, useEffect, useCallback } from 'react';
import {
  AppProvider,
  Frame,
  Page,
  Layout,
  Card,
  Text,
  Button,
  DataTable,
  Badge,
  Banner,
  EmptyState,
  SkeletonBodyText,
  SkeletonDisplayText,
  FooterHelp,
  Link,
  Toast,
  Modal,
  TextContainer,
  List,
  ProgressBar
} from '@shopify/polaris';
import '@shopify/polaris/build/esm/styles.css';
import createApp from '@shopify/app-bridge';
import { getSessionToken } from '@shopify/app-bridge-utils';

// Cache session token briefly to avoid repeated App Bridge roundtrips
let sessionTokenCache = { token: null, expiry: 0 };

// Initialize App Bridge once and cache on window
const initAppBridge = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const host = params.get('host');
    const apiKey = '666b5acfc661291f62004ecc58b0fcfb';
    if (host && apiKey && !window.appBridge) {
      window.appBridge = createApp({ apiKey, host, forceRedirect: true });
      console.log('[AppBridge] Initialized');
    }
  } catch (e) {
    console.error('[AppBridge] init error', e);
  }
};

// Utility to get session token and call fetch with Authorization header
const shopifyFetch = async (url, options = {}) => {
  initAppBridge();
  // If running in an embedded app context
  if (window.appBridge) {
    const app = window.appBridge;
    try {
      let token = null;
      const now = Date.now();
      if (sessionTokenCache.token && sessionTokenCache.expiry > now) {
        token = sessionTokenCache.token;
        console.log('[Auth] Using cached session token');
      } else {
        token = await getSessionToken(app);
        sessionTokenCache = { token, expiry: now + 50_000 }; // ~50s TTL
        console.log('[Auth] Fetched new session token');
      }
      if (!token) console.warn('[Auth] No session token returned by App Bridge');
      else console.log('[Auth] Attaching Authorization bearer token');
      options.headers = {
        ...(options.headers || {}),
        'Authorization': `Bearer ${token}`,
      };
    } catch (e) {
      console.error('Failed to get session token:', e);
    }
  } else {
    const params = new URLSearchParams(window.location.search);
    const host = params.get('host');
    console.warn('[AppBridge] Not initialized. host param:', host);
  }
  // fallback no token
  return fetch(url, options);
};

const API_URL = 'https://google-seller-rating-api.googlestorerating.workers.dev';

function App() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [shop, setShop] = useState('');
  const [storeInfo, setStoreInfo] = useState(null);
  const [error, setError] = useState(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Onboarding states
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1); // 1: Welcome, 2: Plan Selection, 3: Setup Instructions
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isNewInstall, setIsNewInstall] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false); // Track if onboarding was just completed

  // Instructions modal state
  const [showInstructions, setShowInstructions] = useState(false);

  // Helper function to remove charge_id and other onboarding-related params from URL
  const cleanOnboardingParams = () => {
    try {
      const url = new URL(window.location.href);
      const params = url.searchParams;
      
      // Remove onboarding-related parameters
      params.delete('charge_id');
      params.delete('charge');
      params.delete('plan_selected');
      params.delete('installed');
      
      // Update URL without reloading page
      const newUrl = url.pathname + (params.toString() ? '?' + params.toString() : '') + url.hash;
      window.history.replaceState({}, '', newUrl);
      console.log('Cleaned onboarding params from URL');
    } catch (error) {
      console.error('Error cleaning URL params:', error);
    }
  };

  useEffect(() => {
    // Ensure App Bridge is ready before any fetches
    initAppBridge();

    const urlParams = new URLSearchParams(window.location.search);
    const shopDomain = urlParams.get('shop') || 'iwanthisproduct.myshopify.com';
    const installed = urlParams.get('installed') === 'true';
    const chargeAccepted = urlParams.get('charge') === 'accepted';
    const planSelected = urlParams.get('plan_selected') === 'true';
    const chargeId = urlParams.get('charge_id'); // Shopify redirects with charge_id after plan approval

    setShop(shopDomain);
    setIsNewInstall(installed);

    // Handle charge acceptance - user approved plan, show step 2/3
    // charge_id presence indicates user just approved a plan
    if (chargeAccepted || planSelected || chargeId) {
      showToast('Plan activated successfully!');
      // First, try to update the plan in database if it exists in Shopify
      // Then show onboarding step 2/3
      (async () => {
        try {
          // Check if plan exists in Shopify and update our database
          const planCheck = await shopifyFetch(`${API_URL}/api/current-plan?shop=${shopDomain}`);
          if (planCheck.ok) {
            const contentType = planCheck.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const planData = await planCheck.json();
              if (planData.has_plan) {
                // Use plan name or default to 'active' if null
                const planName = planData.plan_name || 'active';
                // Update database with plan
                const updateResponse = await shopifyFetch(`${API_URL}/api/update-plan`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    shop: shopDomain,
                    plan: planName
                  })
                });
                
                if (!updateResponse.ok) {
                  console.error('Failed to update plan in database');
                } else {
                  console.log('Plan updated successfully:', planName);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error updating plan:', error);
        }
        
        // Show onboarding step 2/3 (setup instructions)
        setShowOnboarding(true);
        setOnboardingStep(3);
        // Fetch data but don't let it override onboarding step
        fetchData(shopDomain, false, true); // Pass flag to skip onboarding override
      })();
      return;
    }

    // Fetch data and check installation status
    fetchData(shopDomain, installed, false);
  }, []);

  // Background-load non-critical data after initial UI paint
  useEffect(() => {
    if (!loading && shop) {
      loadRatingsLogs(shop).catch(err => console.error('Background ratings/logs load failed:', err));
    }
  }, [loading, shop]);

  const fetchData = async (shopDomain, justInstalled = false, skipOnboardingOverride = false) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch store info using shopifyFetch
      const statsResponse = await shopifyFetch(`${API_URL}/api/store?shop=${shopDomain}`);
      if (!statsResponse.ok) {
        const errorText = await statsResponse.text();
        console.error('Store fetch error:', errorText);
        throw new Error(`Failed to fetch stats: ${statsResponse.status}`);
      }
      
      // Check if response is JSON
      const contentType = statsResponse.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await statsResponse.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        throw new Error('Server returned non-JSON response');
      }
      
      const statsData = await statsResponse.json();
      setStats(statsData.stats);
      setStoreInfo(statsData.shop);

      // If skipOnboardingOverride is true OR onboardingCompleted, don't modify onboarding state
      // This is used when coming back from plan approval or when refreshing after onboarding
      // But we still need to check and update the plan if charge_id is present
      // And we still need to fetch ratings and logs
      if (skipOnboardingOverride || onboardingCompleted) {
        // ALWAYS close popup if skipOnboardingOverride is true or onboardingCompleted
        // This ensures modal stays closed after completion
        setShowOnboarding(false);
        setOnboardingStep(1); // Reset step
        
        // If already onboarded in database, mark as completed and clean URL
        if (statsData.shop?.onboarded === 1) {
          setOnboardingCompleted(true);
          // Remove charge_id from URL if present
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.has('charge_id')) {
            cleanOnboardingParams();
          }
        }
        
        // Check URL for charge_id - if present, user just approved plan
        const urlParams = new URLSearchParams(window.location.search);
        const chargeId = urlParams.get('charge_id');
        
        if (chargeId && !statsData.shop?.plan) {
          // User just approved plan, check Shopify and update database
          try {
            const planCheck = await shopifyFetch(`${API_URL}/api/current-plan?shop=${shopDomain}`);
            if (planCheck.ok) {
              const contentType = planCheck.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                const planData = await planCheck.json();
                if (planData.has_plan) {
                  // Update database with plan
                  await shopifyFetch(`${API_URL}/api/update-plan`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      shop: shopDomain,
                      plan: planData.plan_name
                    })
                  });
                  // Update local state
                  setStoreInfo({ ...statsData.shop, plan: planData.plan_name });
                }
              }
            }
          } catch (error) {
            console.error('Error updating plan:', error);
          }
        }
        
        // Don't return early - continue to fetch ratings and logs
        // Just skip the onboarding check below
      }

      // If shop doesn't exist in DB and not just installed, create a placeholder record
      if (!statsData.shop && !justInstalled) {
        // This means the user accessed the app directly without OAuth installation
        // We'll show onboarding but they need to go through proper installation
        await fetchPlans(shopDomain);
        setShowOnboarding(true);
        setOnboardingStep(1);
        setLoading(false);
        return;
      }

      // Check if shop needs OAuth (no access token)
      if (statsData.shop && !statsData.shop.access_token && !justInstalled) {
        // Shop exists but no access token - need to go through OAuth
        showToast('App needs to be properly installed. Redirecting to installation...');
        setTimeout(() => {
          initiateOAuth(shopDomain);
        }, 2000);
        setLoading(false);
        return;
      }

      // Check URL for charge_id - if present, user just approved plan, skip plan check
      // BUT skip if onboarding was already completed
      if (!onboardingCompleted) {
        const urlParams = new URLSearchParams(window.location.search);
        const chargeId = urlParams.get('charge_id');
        
        // If charge_id is present, user just approved plan - show step 3
        if (chargeId) {
          // Check if plan exists in Shopify and update database
          try {
            const planCheck = await shopifyFetch(`${API_URL}/api/current-plan?shop=${shopDomain}`);
            if (planCheck.ok) {
              const contentType = planCheck.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                const planData = await planCheck.json();
                if (planData.has_plan) {
                  // Use plan name or default to 'active' if null
                  const planName = planData.plan_name || 'active';
                  // Update database with plan
                  const updateResponse = await shopifyFetch(`${API_URL}/api/update-plan`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      shop: shopDomain,
                      plan: planName
                    })
                  });
                  
                  if (!updateResponse.ok) {
                    console.error('Failed to update plan in database');
                  } else {
                    console.log('Plan updated successfully:', planName);
                    // Update local state
                    setStoreInfo({ ...statsData.shop, plan: planName });
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error updating plan:', error);
          }
          
          // Show onboarding step 2/3 (setup instructions) ONLY if not already completed
          if (!onboardingCompleted) {
            setShowOnboarding(true);
            setOnboardingStep(3);
            setLoading(false);
            return;
          }
        }
      }

      // Check if onboarding is needed (no plan selected OR not onboarded)
      // But skip if already onboarded (onboarded === 1) or if skipOnboardingOverride is true
      // Also skip if onboarding was just completed
      if (!skipOnboardingOverride && !onboardingCompleted) {
        if (statsData.shop?.onboarded === 1) {
          // Already onboarded, don't show onboarding popup
          setShowOnboarding(false);
          setOnboardingCompleted(true); // Mark as completed
        } else if (justInstalled || !statsData.shop?.plan || !statsData.shop?.onboarded) {
          // If just installed, show onboarding step 1 (welcome) instead of redirecting
          if (justInstalled) {
            // Show onboarding step 1 - user will click "Select Plan" to proceed
            setShowOnboarding(true);
            setOnboardingStep(1);
            setLoading(false);
            return;
          }

          // If no plan but not just installed, check if plan exists in Shopify
          // This handles the case when user returns from plan approval
          if (!statsData.shop?.plan) {
            // Try to fetch current plan from Shopify
            try {
              const planCheck = await shopifyFetch(`${API_URL}/api/current-plan?shop=${shopDomain}`);
              if (planCheck.ok) {
                const contentType = planCheck.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                  const planData = await planCheck.json();
                  if (planData.has_plan) {
                    // Use plan name or default to 'active' if null
                    const planName = planData.plan_name || 'active';
                    // Plan exists in Shopify, update our database
                    const updateResponse = await shopifyFetch(`${API_URL}/api/update-plan`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        shop: shopDomain,
                        plan: planName
                      })
                    });
                    
                    if (!updateResponse.ok) {
                      console.error('Failed to update plan in database');
                    } else {
                      console.log('Plan updated successfully:', planName);
                    }

                    // Plan was just added, show onboarding step 2/3 (setup instructions)
                    setShowOnboarding(true);
                    setOnboardingStep(3);
                    setLoading(false);
                    return;
                  }
                }
              }
            } catch (planError) {
              console.error('Error checking current plan:', planError);
              // Continue with onboarding flow even if plan check fails
            }

            // No plan found in DB or Shopify, show onboarding step 1
            setShowOnboarding(true);
            setOnboardingStep(1); // Show welcome, then redirect to pricing
          } else if (!statsData.shop?.onboarded) {
            // Has plan but not onboarded - go to setup (step 2/3)
            setShowOnboarding(true);
            setOnboardingStep(3);
          }
        }
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
      
      // Even if there's an error, try to show onboarding if needed
      // This ensures the user can still complete setup
      // But don't override if skipOnboardingOverride is true
      if (!skipOnboardingOverride && (justInstalled || !storeInfo?.plan || !storeInfo?.onboarded)) {
        setShowOnboarding(true);
        setOnboardingStep(justInstalled ? 1 : (!storeInfo?.plan ? 1 : 3));
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper: background load ratings concurrently
  const loadRatingsLogs = async (shopDomain) => {
    try {
      const ratingsResponse = await shopifyFetch(`${API_URL}/api/ratings?shop=${shopDomain}&limit=10`);

      // Ratings
      if (ratingsResponse.ok) {
        const contentType = ratingsResponse.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const ratingsData = await ratingsResponse.json();
          const ratingsArray = Array.isArray(ratingsData.ratings) ? ratingsData.ratings : (ratingsData.ratings ? [ratingsData.ratings] : []);
          setRatings(ratingsArray);
        } else {
          console.warn('Ratings API returned non-JSON');
          setRatings([]);
        }
      } else {
        setRatings([]);
      }
    } catch (e) {
      console.error('Background load error:', e);
    }
  };

  const initiateOAuth = (shopDomain) => {
    // Redirect to Shopify OAuth
    const apiKey = '666b5acfc661291f62004ecc58b0fcfb'; // Your Shopify API key
    const redirectUri = 'https://google-seller-rating-frontend.pages.dev/auth-callback.html';
    const scopes = 'read_orders,read_customers,write_content';

    const oauthUrl = `https://${shopDomain}/admin/oauth/authorize?client_id=${apiKey}&scope=${scopes}&redirect_uri=${redirectUri}`;

    window.top.location.href = oauthUrl;
  };

  const fetchPlans = async (shopDomain) => {
    try {
      const response = await shopifyFetch(`${API_URL}/api/plans?shop=${shopDomain}`);
      if (!response.ok) throw new Error('Failed to fetch plans');
      const data = await response.json();
      setPlans(data.plans || []);

      // Pre-select free plan
      const freePlan = data.plans?.find(p => p.price === 0);
      if (freePlan) {
        setSelectedPlan(freePlan.id);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setPlans([
        { id: 'free', name: 'Free Plan', price: 0, features: ['Basic features', 'Up to 100 ratings/month'] }
      ]);
      setSelectedPlan('free');
    }
  };

  const handlePlanSubscription = async () => {
    // For Shopify Managed Pricing, redirect to app's billing page
    // Users will select plans directly in Shopify admin
    const billingUrl = `https://admin.shopify.com/store/${shop.replace('.myshopify.com', '')}/apps/google-store-rating/billing`;

    showToast('Redirecting to plan selection...');

    // Redirect to Shopify's app billing page where managed pricing is shown
    setTimeout(() => {
      window.top.location.href = billingUrl;
    }, 1000);
  };

  const completeOnboarding = async () => {
    try {
      // Close modal FIRST before API call to prevent any race conditions
      setShowOnboarding(false);
      setOnboardingStep(1);
      setOnboardingCompleted(true);
      
      // Remove charge_id and other params from URL immediately
      cleanOnboardingParams();
      
      const response = await shopifyFetch(`${API_URL}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop,
          setup_completed: true
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to complete onboarding: ${response.status}`);
      }

      // Update local state immediately to prevent popup from reappearing
      if (storeInfo) {
        setStoreInfo({ ...storeInfo, onboarded: 1 });
      }
      
      showToast('Setup completed! Welcome to Google Seller Rating!');
      
      // Wait a moment for database update to propagate, then refresh data
      // Use skipOnboardingOverride to prevent modal from reopening
      setTimeout(() => {
        fetchData(shop, false, true);
      }, 1000);
    } catch (err) {
      console.error('Onboarding completion error:', err);
      showToast(`Error: ${err.message}`);
      // On error, don't reopen modal - user can refresh manually if needed
    }
  };

  const showToast = (message) => {
    setToastMessage(message);
    setToastActive(true);
  };

  const toggleToastActive = useCallback(() => setToastActive((active) => !active), []);

  // Removed Logs page support

  const refreshData = () => {
    if (shop) {
      // Always skip onboarding check when refreshing to prevent popup from reappearing
      // The fetchData function will check onboarded status and close popup if needed
      fetchData(shop, false, true);
      showToast('Data refreshed');
    }
  };

  const calculateAverage = () => {
    if (!stats || !stats.ratings_breakdown || stats.ratings_breakdown.length === 0) {
      return '0.0';
    }
    const total = stats.ratings_breakdown.reduce((sum, r) => sum + r.count, 0);
    const weightedSum = stats.ratings_breakdown.reduce((sum, r) => sum + (r.rating_value * r.count), 0);
    return total > 0 ? (weightedSum / total).toFixed(1) : '0.0';
  };

  const recommendedAppsFooter = (
    <FooterHelp>
      <div style={{ marginBottom: '8px' }}>
        <Text variant="headingSm" as="h3">Recommended Apps</Text>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Link url="https://apps.shopify.com/order-tracking-pro" external>📧 Order Tracking Page Pro</Link>
      </div>
      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e1e3e5' }}>
        <Link url="https://google-seller-rating-frontend.pages.dev/privacy-policy.html" external>
          Privacy Policy
        </Link>
      </div>
    </FooterHelp>
  );

  const toastMarkup = toastActive ? (
    <Toast content={toastMessage} onDismiss={toggleToastActive} />
  ) : null;

  const InstructionsModal = () => (
    <Modal
      open={showInstructions}
      onClose={() => setShowInstructions(false)}
      title="Setup Instructions"
      primaryAction={{
        content: 'Got it!',
        onAction: () => setShowInstructions(false)
      }}
    >
      <Modal.Section>
        <TextContainer>
          <Text variant="headingMd" as="h2">How to Set Up Google Seller Rating</Text>
          <Banner status="info">
            <p>Follow these steps to enable the rating widget on your store:</p>
          </Banner>

          <Text variant="headingSm" as="h3">Step 1: Configure Extension Settings</Text>
          <List type="number">
            <List.Item>
              Go to <strong>Settings → Checkout</strong> in your Shopify admin
            </List.Item>
            <List.Item>
              Scroll down to "Thankyou Page" and click <strong>"Customize"</strong>
            </List.Item>
            <List.Item>
              In the editor, click <strong>"Add app block"</strong> and select <strong>"Customer Rating Block"</strong>
            </List.Item>
            <List.Item>
              Configure the following settings:
              <List>
                <List.Item><strong>Google Merchant Center ID:</strong> Your Google Merchant ID (e.g., 123456789)</List.Item>
                <List.Item><strong>Estimated Delivery Days:</strong> Number of days for delivery (default: 7)</List.Item>
                <List.Item><strong>Default Country:</strong> Default delivery country code (e.g., IN, US, GB)</List.Item>
              </List>
            </List.Item>
            <List.Item>
              Click <strong>"Save"</strong> to publish your changes
            </List.Item>
          </List>

          <div style={{ marginTop: '16px' }}>
            <Button
              url={`https://admin.shopify.com/store/${shop.replace('.myshopify.com', '')}/settings/checkout/editor`}
              external
              fullWidth
            >
              Open Checkout Editor
            </Button>
          </div>

          <div style={{ marginTop: '24px' }}>
            <Text variant="headingSm" as="h3">Step 2: Get Your Google Merchant ID</Text>
            <List type="number">
              <List.Item>
                Sign up for <Link url="https://merchants.google.com" external>Google Merchant Center</Link> if you haven't already
              </List.Item>
              <List.Item>
                Your Merchant ID is displayed in the top right corner of Merchant Center
              </List.Item>
              <List.Item>
                Copy this ID and paste it into the extension settings
              </List.Item>
            </List>
          </div>

          <div style={{ marginTop: '24px' }}>
            <Text variant="headingSm" as="h3">Step 3: Test the Widget</Text>
            <List type="number">
              <List.Item>
                Place a test order on your store
              </List.Item>
              <List.Item>
                Check the Thank You page - you should see the rating widget
              </List.Item>
              <List.Item>
                Submit a test rating and verify it appears in your dashboard
              </List.Item>
            </List>
          </div>

          <div style={{ marginTop: '16px' }}>
            <Banner status="success">
              <p>Need help? Check out the <Link url="https://support.google.com/merchants/answer/7124326" external>Google Seller Ratings documentation</Link></p>
            </Banner>
          </div>
        </TextContainer>
      </Modal.Section>
    </Modal>
  );

  const OnboardingModal = () => {
    const progressValue = (onboardingStep / 3) * 100;
    const needsInstallation = storeInfo && !storeInfo.access_token;

    const handlePrimaryAction = () => {
      if (needsInstallation && onboardingStep === 1) {
        initiateOAuth(shop);
      } else if (onboardingStep === 1) {
        // Redirect to Shopify managed pricing instead of showing step 2
        const pricingUrl = `https://admin.shopify.com/store/${shop.replace('.myshopify.com', '')}/charges/google-store-rating/pricing_plans`;
        showToast('Redirecting to plan selection...');
        setTimeout(() => {
          window.top.location.href = pricingUrl;
        }, 1000);
      } else if (onboardingStep === 3) {
        completeOnboarding();
      }
    };

    return (
      <Modal
        open={showOnboarding}
        onClose={() => { }}
        title={onboardingStep === 1 ? 'Welcome to Google Seller Rating!' : 'Complete Setup'}
        primaryAction={{
          content: needsInstallation && onboardingStep === 1 ? 'Install App Properly' : (onboardingStep === 3 ? 'Complete Setup' : 'Select Plan'),
          onAction: handlePrimaryAction
        }}
        secondaryActions={[]}
      >
        <Modal.Section>
          <div style={{ marginBottom: '16px' }}>
            <ProgressBar progress={progressValue} size="small" />
            <Text variant="bodySm" as="p" color="subdued" alignment="center">
              Step {onboardingStep === 3 ? 2 : 1} of 2
            </Text>
          </div>

          {needsInstallation && onboardingStep === 1 && (
            <div style={{ marginBottom: '16px' }}>
              <Banner status="warning" title="Proper Installation Required">
                <p>To enable paid plans and full app functionality, please install the app properly through Shopify OAuth.</p>
                <p>Click "Install App Properly" to complete the installation process.</p>
              </Banner>
            </div>
          )}

          {onboardingStep === 1 && (
            <TextContainer>
              <Text variant="headingMd" as="h2">Let's Get Started!</Text>
              <p>Welcome! This app helps you collect Google Seller Ratings from your customers.</p>
              <List type="number">
                <List.Item>Select a pricing plan from Shopify</List.Item>
                <List.Item>Add the rating block to your Thank You page</List.Item>
                <List.Item>Start collecting ratings automatically</List.Item>
              </List>
              <p>Click "Select Plan" to choose your pricing tier in Shopify's billing page.</p>
            </TextContainer>
          )}

          {onboardingStep === 3 && (
            <TextContainer>
              <Text variant="headingMd" as="h2">Add Rating Block to Your Store</Text>
              <Banner status="info">
                <p>Follow these steps to enable the rating widget on your Thank You pages</p>
              </Banner>
              <List type="number">
                <List.Item>
                  <strong>Go to Checkout Settings:</strong> Navigate to Settings → Checkout in your Shopify admin
                </List.Item>
                <List.Item>
                  <strong>Customize Thank You Page:</strong> Scroll down to "Thankyou Page" and click "Customize"
                </List.Item>
                <List.Item>
                  <strong>Add Rating Block:</strong> In the editor, click "Add app block" and select "Customer Rating Block"
                </List.Item>
                <List.Item>
                  <strong>Configure Settings:</strong> Enter your Google Merchant Center ID and estimated delivery days
                </List.Item>
                <List.Item>
                  <strong>Save Changes:</strong> Click "Save" to publish your changes
                </List.Item>
              </List>
              <div style={{ marginTop: '16px' }}>
                <Button
                  url={`https://admin.shopify.com/store/${shop.replace('.myshopify.com', '')}/settings/checkout/editor`}
                  external
                  fullWidth
                >
                  Open Checkout Editor
                </Button>
              </div>
            </TextContainer>
          )}
        </Modal.Section>
      </Modal>
    );
  };

  // Dashboard Page
  const DashboardPage = () => {
    const ratingsTableRows = ratings.map(rating => [
      rating.order_id || 'N/A',
      `${rating.rating_value || 0} ⭐`,
      (rating.optin_attempted === 1 || rating.optin_attempted === true) ? <Badge status="success">Yes</Badge> : <Badge>No</Badge>,
      rating.created_at ? new Date(rating.created_at).toLocaleDateString() : 'N/A'
    ]);

    const breakdownRows = stats?.ratings_breakdown?.map(item => [
      `${item.rating_value} ⭐`,
      item.count.toString()
    ]) || [];

    return (
      <Page
        title="Dashboard"
        subtitle="Google Seller Rating Analytics"
        primaryAction={{
          content: 'Refresh Data',
          onAction: refreshData
        }}
        secondaryActions={[{
          content: 'Instructions',
          onAction: () => setShowInstructions(true)
        }]}
      >
        <ui-nav-menu>
          <a href={`?shop=${shop}&page=dashboard`}>Dashboard</a>
          <a href={`?shop=${shop}&page=plans`}>Plans</a>
          <a href={`?shop=${shop}&page=settings`}>Settings</a>
        </ui-nav-menu>

        {error && (
          <Layout>
            <Layout.Section>
              <Banner status="critical" title="Error loading data">
                <p>{error}</p>
              </Banner>
            </Layout.Section>
          </Layout>
        )}

        <Layout>
          <Layout.Section>
            <Layout>
              <Layout.Section oneThird>
                <Card>
                  <div style={{ padding: '16px' }}>
                    <Text variant="headingMd" as="h2" color="subdued">Total Ratings</Text>
                    <div style={{ fontSize: '32px', fontWeight: '600', marginTop: '8px', color: '#202223' }}>
                      {stats?.total_ratings || 0}
                    </div>
                  </div>
                </Card>
              </Layout.Section>

              <Layout.Section oneThird>
                <Card>
                  <div style={{ padding: '16px' }}>
                    <Text variant="headingMd" as="h2" color="subdued">Opt-in Attempts</Text>
                    <div style={{ fontSize: '32px', fontWeight: '600', marginTop: '8px', color: '#202223' }}>
                      {stats?.optin_attempts || 0}
                    </div>
                  </div>
                </Card>
              </Layout.Section>

              <Layout.Section oneThird>
                <Card>
                  <div style={{ padding: '16px' }}>
                    <Text variant="headingMd" as="h2" color="subdued">Average Rating</Text>
                    <div style={{ fontSize: '32px', fontWeight: '600', marginTop: '8px', color: '#202223' }}>
                      {calculateAverage()} ⭐
                    </div>
                  </div>
                </Card>
              </Layout.Section>
            </Layout>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <div style={{ padding: '16px' }}>
                <Text variant="headingLg" as="h2">Ratings Breakdown by Star</Text>
              </div>
              {breakdownRows.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'numeric']}
                  headings={['Rating', 'Count']}
                  rows={breakdownRows}
                />
              ) : (
                <div style={{ padding: '40px 16px' }}>
                  <EmptyState
                    heading="No ratings data yet"
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <p>Rating breakdown will appear here once customers submit ratings.</p>
                  </EmptyState>
                </div>
              )}
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <div style={{ padding: '16px' }}>
                <Text variant="headingLg" as="h2">Recent Ratings</Text>
              </div>
              {ratings.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['Order ID', 'Rating', 'Google Opt-in Attempt', 'Date']}
                  rows={ratingsTableRows}
                />
              ) : (
                <div style={{ padding: '40px 16px' }}>
                  <EmptyState
                    heading="No ratings yet"
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <p>Ratings will appear here once customers submit them.</p>
                  </EmptyState>
                </div>
              )}
            </Card>
          </Layout.Section>
        </Layout>
        {recommendedAppsFooter}
      </Page>
    );
  };

  // Plans Page
  const PlansPage = () => (
    <Page
      title="💳 Plans"
      subtitle="Manage your subscription"
    >
      <ui-nav-menu>
        <a href={`?shop=${shop}&page=dashboard`}>Dashboard</a>
        <a href={`?shop=${shop}&page=plans`}>Plans</a>
        <a href={`?shop=${shop}&page=settings`}>Settings</a>
      </ui-nav-menu>

      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: '24px' }}>
              <Text variant="headingLg" as="h2">Current Plan</Text>
              <div style={{ marginTop: '16px', padding: '16px', background: '#f6f6f7', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text variant="headingMd" as="h3">{storeInfo?.plan || 'Free Plan'}</Text>
                    <Text as="p" color="subdued">Active subscription</Text>
                  </div>
                  <Badge status="success">Active</Badge>
                </div>
              </div>
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: '24px' }}>
              <Text variant="headingLg" as="h2">Manage Your Plan</Text>
              <div style={{ marginTop: '16px' }}>
                <Text as="p" color="subdued">
                  Visit Shopify billing to manage your subscription.
                </Text>
                <div style={{ marginTop: '16px' }}>
                  <Button
                    primary
                    url={`https://admin.shopify.com/store/${shop.replace('.myshopify.com', '')}/charges/google-store-rating/pricing_plans`}
                    external
                  >
                    Manage Plan
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
      {recommendedAppsFooter}
    </Page>
  );

  // Settings Page
  const SettingsPage = () => (
    <Page
      title="⚙️ Settings"
      subtitle="App configuration and information"
    >
      <ui-nav-menu>
        <a href={`?shop=${shop}&page=dashboard`}>Dashboard</a>
        <a href={`?shop=${shop}&page=plans`}>Plans</a>
        <a href={`?shop=${shop}&page=settings`}>Settings</a>
      </ui-nav-menu>

      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: '24px' }}>
              <Text variant="headingLg" as="h2">Store Information</Text>
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #e1e3e5' }}>
                  <Text as="span" color="subdued">Store Domain</Text>
                  <Text as="span" fontWeight="semibold">{shop}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #e1e3e5' }}>
                  <Text as="span" color="subdued">App Status</Text>
                  <Badge status="success">Active</Badge>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0' }}>
                  <Text as="span" color="subdued">Installed Date</Text>
                  <Text as="span" fontWeight="semibold">
                    {storeInfo?.installed_at ? new Date(storeInfo.installed_at).toLocaleDateString() : 'N/A'}
                  </Text>
                </div>
              </div>
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: '24px' }}>
              <Text variant="headingLg" as="h2">Documentation</Text>
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Link url="https://support.google.com/merchants/answer/7124326" external>
                  📘 Google Seller Ratings Program Overview
                </Link>
                <Link url="https://support.google.com/merchants/answer/9916743" external>
                  📝 How to collect seller ratings
                </Link>
                <Link url="https://google-seller-rating-frontend.pages.dev/privacy-policy.html" external>
                  🔒 Privacy Policy
                </Link>
              </div>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
      {recommendedAppsFooter}
    </Page>
  );

  const renderPage = () => {
    if (loading) {
      return (
        <Page title="Loading...">
          <Layout>
            <Layout.Section>
              <Card>
                <SkeletonDisplayText size="medium" />
                <SkeletonBodyText lines={3} />
              </Card>
            </Layout.Section>
          </Layout>
        </Page>
      );
    }

    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get('page') || 'dashboard';

    switch (page) {
      case 'dashboard':
        return <DashboardPage />;
      case 'plans':
        return <PlansPage />;
      case 'settings':
        return <SettingsPage />;
      default:
        return <DashboardPage />;
    }
  };

  return (
    <AppProvider>
      <Frame>
        {renderPage()}
        {toastMarkup}
        <OnboardingModal />
        <InstructionsModal />
      </Frame>
    </AppProvider>
  );
}

export default App;
